import React, { useState, useEffect } from 'react';
import styles from './subCategories.module.css';
import { IconButton, InputBase, Paper } from '@mui/material';
import { Search } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const SearchInput = styled(Paper)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    width: '160px',
    fontFamily: `'Public Sans', sans-serif`,
    margin: '10px',
    height: "30px"
}));

const ProductFilter = ({ filterData, onFilterChange, filter, count, loading, handleSubmit, stock, status, handleCheckboxchange, totalItems, handleReset, search }) => {
    const mapFilterProp = (filterArr) => {
        if (!filterArr || !Array.isArray(filterArr) || filterArr.length === 0) return {};
        const mapped = {};
        filterArr.forEach(item => {
            if (item.attribute && item.values) {
                mapped[item.attribute] = item.values;
            }
        });
        return mapped;
    };

    const [selectedFilters, setSelectedFilters] = useState(() => mapFilterProp(filter));
    const [searchTerms, setSearchTerms] = useState({});

    // Sync selectedFilters when filter prop is updated from parent (on load, refresh, apply, or reset)
    useEffect(() => {
        setSelectedFilters(mapFilterProp(filter));
    }, [filter]);

    // Helper for case-insensitive and flexible attribute lookup
    const getSelectedForAttribute = (attrName) => {
        if (!attrName) return null;
        let currentMap = selectedFilters;
        if (!currentMap || Object.keys(currentMap).length === 0) {
            currentMap = mapFilterProp(filter);
        }
        const attrLower = attrName.toLowerCase().trim();
        const matchingKey = Object.keys(currentMap).find(k => {
            const kLower = k.toLowerCase().trim();
            return kLower === attrLower || 
                   (attrLower.includes('manufacturer') && kLower.includes('manufacturer')) ||
                   (attrLower.includes('mfr') && kLower.includes('mfr'));
        });
        return matchingKey ? currentMap[matchingKey] : null;
    };

    // Handle attribute selection - SINGLE SELECT PER COLUMN
    const handleSelect = (attribute, value) => {
        setSelectedFilters(prev => {
            const newFilters = { ...prev };

            const existingKey = Object.keys(newFilters).find(
                k => k.toLowerCase().trim() === attribute.toLowerCase().trim()
            ) || attribute;

            const currentValues = newFilters[existingKey] || [];
            const isAlreadySelected = currentValues.some(
                v => v.toLowerCase().trim() === value.toLowerCase().trim()
            );

            if (isAlreadySelected) {
                // If already selected, deselect it (toggle off)
                delete newFilters[existingKey];
            } else {
                // Single-select: replace with only the newly selected value
                delete newFilters[existingKey];
                newFilters[attribute] = [value];
            }

            const formattedFilters = Object.entries(newFilters).map(([attr, vals]) => ({
                attribute: attr,
                values: Array.isArray(vals) ? vals : [vals]
            }));

            if (onFilterChange) {
                onFilterChange(formattedFilters);
            }

            return newFilters;
        });
    };

    // Filter values based on search term and selected manufacturer state
    const filteredValues = (filter) => {
        const searchTerm = searchTerms[filter.attribute]?.toLowerCase() || '';
        let values = filter.values;

        // If Manufacturer column is active, show only the selected manufacturer(s)
        const isManufacturerCol = filter.attribute.toLowerCase().includes('manufacturer') || filter.attribute.toLowerCase().includes('mfr');
        const selected = getSelectedForAttribute(filter.attribute);

        if (isManufacturerCol && selected && Array.isArray(selected) && selected.length > 0) {
            values = values.filter(v => 
                selected.some(sel => {
                    const s = sel.toLowerCase().trim();
                    const val = v.toLowerCase().trim();
                    return s === val || s.includes(val) || val.includes(s);
                })
            );
        }

        return values.filter(value =>
            value.toLowerCase().includes(searchTerm)
        );
    };

    // Handle search input change
    const handleSearchChange = (attribute, e) => {
        setSearchTerms(prev => ({
            ...prev,
            [attribute]: e.target.value
        }));
    };

    const onClickReset = () => {
        setSelectedFilters({});
        setSearchTerms({});
        if (onFilterChange) {
            onFilterChange([]);
        }
        if (handleReset) {
            handleReset();
        }
    };

    const hasActiveFilters =
        (filter && filter.length > 0) ||
        Object.keys(selectedFilters).length > 0 ||
        status ||
        stock ||
        (search && search !== "") ||
        Object.values(searchTerms).some(term => term && term.trim() !== "");

    return (
        <div className={styles.listMain}>
            {filterData?.length > 0 && (totalItems === undefined || totalItems > 0) &&
                <div className={styles.listCard}>
                    {filterData?.map((filter) => {
                        const valuesToDisplay = filteredValues(filter);
                        if (!valuesToDisplay || valuesToDisplay.length === 0) return null;
                        const selectedValues = getSelectedForAttribute(filter.attribute);
                        return (
                            <div key={filter.attribute} className={styles.listCardCol}>
                                <div className={styles.listColTitle}>{filter.attribute}</div>
                                <div className={styles.listItems}>
                                    <SearchInput>
                                        <IconButton aria-label="search">
                                            <Search sx={{ fontSize: "16px !important" }} />
                                        </IconButton>
                                        <InputBase
                                            placeholder="Search…"
                                            sx={{ fontSize: "12px !important" }}
                                            value={searchTerms[filter.attribute] || ''}
                                            onChange={(e) => handleSearchChange(filter.attribute, e)}
                                        />
                                    </SearchInput>
                                    {valuesToDisplay.map((value) => {
                                        const isSelected = selectedValues && Array.isArray(selectedValues) &&
                                            selectedValues.some(sel => sel.toLowerCase().trim() === value.toLowerCase().trim());
                                        return (
                                            <div
                                                key={value}
                                                className={`${styles.hoverableSpan} ${isSelected ? styles.selected : ""}`}
                                                onClick={() => handleSelect(filter.attribute, value)}
                                            >
                                                {value}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            }
            <div className={styles.subCatcheck}>
                <div className={styles.subCatcheckCol}>
                    <label>Stock</label>
                    <div className={styles.checkalign}>
                        <input type="checkbox" checked={stock} onChange={() => handleCheckboxchange('stock')} /> <span>in Stock</span>
                    </div>

                </div>
                <div className={styles.subCatcheckCol}>
                    <label>status</label>
                    <div className={styles.checkalign}>
                        <input type="checkbox" checked={status} onChange={() => handleCheckboxchange('status')} /> <span>active</span>
                    </div>

                </div>

            </div>

            <div className={styles.loginAccLinks}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                    <button type="submit"
                        onClick={handleSubmit}
                        disabled={loading || count <= 0 || (filter?.length <= 0 && !status && !stock)} 
                        className={styles.loginButton}>
                        Apply all
                    </button>
                    <button type="submit" 
                        className={styles.loginButtonStroke}
                        disabled={loading || !hasActiveFilters}
                        onClick={onClickReset}>
                        Reset
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductFilter;




// <div className={styles.subCatcheck}>
// <div className={styles.subCatcheckCol}>
//     <label>Stock</label>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
// </div>
// <div className={styles.subCatcheckCol}>
//     <label>Title</label>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
// </div>
// <div className={styles.subCatcheckCol}>
//     <label>Title</label>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
//     <div className={styles.checkalign}>
//         <input type="checkbox" /> <span>Dummy content</span>
//     </div>
// </div>

// </div>

// <div className={styles.loginAccLinks}>
// <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
//     <div>
//         <button type="submit" className={styles.loginButton}>Apply all </button>
//     </div>
//     <div>
//         <button type="submit" className={styles.loginButtonStroke}>Reset</button>
//     </div>

// </div>
// </div>